import {useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {RotateCcw, Search} from "lucide-react";
import {useSearchParams} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime} from "../../shared/utils/format";
import {listPostedRefunds, refundKeys} from "./api";
import {getRefundStatusPresentation} from "./statusMachine";
import {useTranslation} from "../../shared/i18n";
import "./refunds.css";

const initialFilters = (searchParams) => ({
    status: searchParams.get("status") === "posted" ? "posted" : "",
    payment_bill_id: (searchParams.get("payment_bill_id") || "").trim(),
});

export const RefundHistoryPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const [searchParams] = useSearchParams();
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const [draftFilters, setDraftFilters] = useState(() => initialFilters(searchParams));
    const [filters, setFilters] = useState(() => initialFilters(searchParams));
    const [page, setPage] = useState(1);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            setCountryScope(nextScope == null ? null : String(nextScope));
            setPage(1);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const historyQuery = useQuery({
        queryKey: refundKeys.history(countryScope, filters, page),
        queryFn: () => listPostedRefunds({countryScope, page, filters}),
        enabled: countryScope !== null,
        retry: false,
    });

    const submitFilters = (event) => {
        event.preventDefault();
        setFilters({...draftFilters, payment_bill_id: draftFilters.payment_bill_id.trim()});
        setPage(1);
    };

    const resetFilters = () => {
        const empty = {status: "", payment_bill_id: ""};
        setDraftFilters(empty);
        setFilters(empty);
        setPage(1);
    };

    const columns = [
        {
            key: "payment",
            label: t("money.refunds.historyPage.payment"),
            render: (item) => (
                <div>
                    <span className="admin-strong">{item.pay_bill_id}</span>
                    <div className="admin-muted">{t("money.refunds.historyPage.refundId", {id: item.id})}</div>
                </div>
            ),
        },
        {
            key: "status",
            label: t("money.refunds.historyPage.typeStatus"),
            render: (item) => {
                const status = getRefundStatusPresentation(item.status);
                return (
                    <div>
                        <span>{item.refund_type === "full" ? t("money.refunds.historyPage.full") : t("money.refunds.historyPage.partial")}</span>
                        <div><StatusBadge tone={status.tone}>{t(`money.refunds.statuses.${status.known ? item.status : "unknown"}`)}</StatusBadge></div>
                    </div>
                );
            },
        },
        {
            key: "amount",
            label: t("money.refunds.historyPage.total"),
            render: (item) => <span className="admin-strong is-money">{formatExactMoney(item.amount)}</span>,
        },
        {
            key: "main_amount",
            label: t("money.refunds.historyPage.main"),
            render: (item) => formatExactMoney(item.main_amount),
        },
        {
            key: "points_amount",
            label: t("money.refunds.historyPage.points"),
            render: (item) => formatExactMoney(item.points_amount),
        },
        {
            key: "cashup_amount",
            label: "Cashup",
            render: (item) => formatExactMoney(item.cashup_amount),
        },
        {key: "reason", label: t("money.refunds.historyPage.reason"), render: (item) => item.reason || "—"},
        {
            key: "creator",
            label: t("money.refunds.historyPage.creator"),
            render: (item) => (
                <div>
                    <span>{item.created_by_name || "—"}</span>
                    <div className="admin-muted">
                        {item.created_by_id === null ? t("money.refunds.historyPage.system") : `#${item.created_by_id}`}
                    </div>
                </div>
            ),
        },
        {
            key: "created_at",
            label: t("money.refunds.historyPage.posted"),
            render: (item) => formatDateTime(item.created_at),
        },
    ];

    const data = historyQuery.data;

    return (
        <div className="admin-page refund-history-page">
            <header className="admin-page-header refund-history-header">
                <div>
                    <h1 className="admin-page-title">{t("money.refunds.historyPage.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.refunds.historyPage.copy")}
                    </p>
                </div>
            </header>

            <form className="admin-toolbar refund-filter-bar" onSubmit={submitFilters}>
                <label className="admin-field">
                    <span>{t("money.refunds.historyPage.status")}</span>
                    <select
                        aria-label={t("money.refunds.historyPage.status")}
                        value={draftFilters.status}
                        onChange={(event) =>
                            setDraftFilters((current) => ({...current, status: event.target.value}))}
                    >
                        <option value="">{t("money.refunds.historyPage.allStatuses")}</option>
                        <option value="posted">{t("money.refunds.statuses.posted")}</option>
                    </select>
                </label>
                <label className="admin-field refund-history-bill-filter">
                    <span>{t("money.refunds.historyPage.billId")}</span>
                    <input
                        aria-label={t("money.refunds.historyPage.billId")}
                        type="text"
                        value={draftFilters.payment_bill_id}
                        onChange={(event) => setDraftFilters((current) => ({
                            ...current,
                            payment_bill_id: event.target.value,
                        }))}
                    />
                </label>
                <button className="admin-button admin-button-primary" type="submit">
                    <Search aria-hidden="true" size={16} /> {t("money.refunds.historyPage.apply")}
                </button>
                <button
                    className="admin-button admin-button-secondary"
                    type="button"
                    onClick={resetFilters}
                    title={t("money.refunds.historyPage.resetTitle")}
                >
                    <RotateCcw aria-hidden="true" size={16} /> {t("money.refunds.historyPage.reset")}
                </button>
            </form>

            {historyQuery.isError ? (
                <DataState
                    title={t("money.refunds.historyPage.loadError")}
                    copy={mapFinanceError(historyQuery.error).message}
                />
            ) : (
                <>
                    <AdminTable
                        columns={columns}
                        rows={data?.results || []}
                        loading={historyQuery.isLoading}
                        emptyText={t("money.refunds.historyPage.empty")}
                    />
                    {data ? (
                        <ServerPagination
                            count={data.count}
                            page={page}
                            pageSize={20}
                            previous={data.previous}
                            next={data.next}
                            pending={historyQuery.isFetching}
                            onPageChange={setPage}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
};
